import { getDoc, getDocs } from "firebase/firestore";
import {
  businessCollection,
  businessProfileRef,
  businessSettingsRef,
} from "./firestorePaths";

const str = (v) => (typeof v === "string" ? v.trim() : "");

function normalizeBusiness(data = {}) {
  return {
    businessId: str(data.businessId),
    name: str(data.businessName) || "My business",
    type: str(data.businessType),
    image: data.businessImage || null,
    phone: data.businessPhone || null,
    email: data.businessEmail || null,
    // An object {address, title, latitude, longitude}, not a string.
    address: data.businessAddress || null,
    stores: Array.isArray(data.businessStores) ? data.businessStores : [],
    accountStatus: str(data.accountStatus) || "Unknown",
    subscriptionTier: str(data.subscriptionTier) || "Trial",
    userIds: Array.isArray(data.businessUsers) ? data.businessUsers : [],
  };
}

/**
 * Store handle for the URL, e.g. /store/zeri/orders.
 *
 * Prefers the subdomain of the storefront URL (zeri.stocko.shop -> "zeri") so
 * the admin URL matches the shop the merchant already knows. Falls back to a
 * slug of the business name plus a short uid suffix, which keeps it readable
 * and distinct for businesses that never set up a catalogue.
 *
 * Purely cosmetic: the business is resolved from the signed-in user, never
 * from this value, so a stale handle cannot grant or deny access.
 */
export function storeHandle(business = {}, settings = {}) {
  const storeUrl = str(settings.store?.url);
  if (storeUrl) {
    const sub = storeUrl.replace(/^https?:\/\//, "").split(".")[0];
    if (sub) return sub.toLowerCase();
  }

  const slug = str(business.businessName)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  const suffix = str(business.businessId).replace(/^Bus-/, "").slice(-4).toLowerCase();
  if (slug) return suffix ? `${slug}-${suffix}` : slug;
  return suffix || "store";
}

function normalizeSettings(data = {}) {
  // The POS writes these lowercase. The website signup writes them capitalized
  // (General/Financial/Receipt/Shift), which does not match what the app reads -
  // accept both so either origin resolves.
  const general = data.general || data.General || {};
  const financial = data.financial || data.Financial || {};
  // general.currency is blank in practice; financial.name carries the ISO code
  // ("LSL"), which matches what order and transaction totals are stamped with.
  const currency = str(general.currency) || str(financial.name);

  // Statutory levies come from the Lekuka tax configuration, not financial.levies
  // (which is empty). Anything typed as a LEVY is one; VAT entries are not.
  const lekukaLevies = (financial.lekuka?.applicableTaxes || []).filter((t) =>
    String(t.taxType || "").includes("LEVY")
  );

  return {
    currency,
    allowDebt: Boolean(general.allowDebt),
    sorting: str(general.sorting) || "Date",
    tax: {
      collect: Boolean(financial.collect),
      name: str(financial.name),
      description: str(financial.desc) || "VAT",
      rate: Number(financial.amountPer) || 0,
      type: str(financial.type) || "Percentage",
      factoring: str(financial.factoring) || "Included",
    },
    // Levies are settings, not a collection - the Finance tab reads them here.
    levies: [
      ...(Array.isArray(financial.levies) ? financial.levies : []),
      ...lekukaLevies.map((t) => ({
        id: String(t.taxID),
        name: t.taxName,
        type: t.taxType === "FIXED_VALUE_LEVY" ? "Fixed" : "Percentage",
        // This block comes from the Lekuka/RSL tax configuration, not from the
        // POS, so its rates are plain numbers rather than the cents everything
        // else uses. Percentages pass through; fixed amounts are scaled to
        // cents so the Finance tab can format them like any other money.
        amountPer:
          t.taxType === "FIXED_VALUE_LEVY"
            ? (t.taxRate ?? 0) * 100
            : (t.taxRate ?? 0),
        collect: true,
      })),
    ],
    roles: Array.isArray(data.roles) ? data.roles : [],
    variants: Array.isArray(data.variants) ? data.variants : [],
    // Order status ids resolve against this list - see ordersApi.
    states: Array.isArray(data.states) ? data.states : [],
    // Storefront identity; storeHandle() derives the admin URL slug from it.
    store: {
      url: str(data.catalogue?.storeIdentity?.storeUrl),
      name: str(data.catalogue?.storeIdentity?.storeName),
      tagline: str(data.catalogue?.storeIdentity?.tagline),
    },
  };
}

// users.role is an object {id, title} in Firestore, not a string.
const roleTitle = (role) => {
  if (typeof role === "string") return role.trim();
  if (role && typeof role === "object") return str(role.title);
  return "";
};

export async function fetchBusinessProfile(businessId) {
  if (!businessId) return null;
  const snap = await getDoc(businessProfileRef(businessId));
  return snap.exists() ? normalizeBusiness(snap.data()) : null;
}

export async function fetchBusinessSettings(businessId) {
  if (!businessId) return null;
  const snap = await getDoc(businessSettingsRef(businessId));
  return snap.exists() ? normalizeSettings(snap.data()) : null;
}

/**
 * uid -> display name for the business's staff.
 *
 * Transactions store only the transactor's uid, so this is what turns a
 * transaction row into a readable cashier name.
 */
export async function fetchBusinessUsers(businessId) {
  if (!businessId) return {};
  const snap = await getDocs(businessCollection(businessId, "users"));
  const byId = {};
  snap.forEach((docSnap) => {
    const data = docSnap.data() || {};
    byId[docSnap.id] = {
      id: docSnap.id,
      name: str(data.userName) || str(data.email) || docSnap.id,
      email: str(data.email),
      role: roleTitle(data.role) || "Staff",
    };
  });
  return byId;
}
